/**
 * @module Components.Routes
 *
 */
import React, { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import styles from "./styles.module.scss";
import { useHistory, useLocation } from "react-router-dom";
import Offerings from "./Offerings";
import Setup from "./Setup";
import Customers from "./Customers";
import Navbar from "app/components/Navbar";
import { useTranslation } from "react-i18next";
import Tabs from "app/components/Tabs";
import { dismissSetupRail } from "app/components/AcademySetup/SetupToast";
import { UserContext } from "app/contexts/UserContext";

export default function Billing() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  // The tabs are local state, not routes, so the sidebar's route watcher
  // never sees a switch; spec §4 still counts it as leaving the view.
  useEffect(() => {
    dismissSetupRail();
  }, [selectedTab]);
  const userId = useSelector((state: RootState) => state.user.self.id);
  const history = useHistory();
  const [stripeId, setStripeId] = useState(null);
  const [activatedAccount, setActivatedAccount] = useState(null);
  const [enabledIntegration, setEnabledIntegration] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const location = useLocation();
  const user = useContext(UserContext);

  const shouldRedirectToFacility = user.facility?.hasFacilityLevelStripe;

  // Redirect to facility billing when facility has stripe flag enabled.
  // Keep router state so the Academy Setup coachmark survives the redirect.
  useEffect(() => {
    if (shouldRedirectToFacility) {
      history.replace({
        pathname: "/facility_billing",
        search: location.search,
        state: location.state,
      });
    }
  }, [shouldRedirectToFacility, history, location.search, location.state]);

  useEffect(() => {
    if (shouldRedirectToFacility) {
      return; // Skip fetching coach profile if redirecting
    }
    setIsLoadingData(true);
    const fetchCoachProfile = async () => {
      try {
        const profileData = await schedulingFetchGet(
          `coach_profiles/${userId}`,
        );
        setStripeId(profileData.stripeAccountId);
        setEnabledIntegration(profileData.stripeIntegrationEnabled);
        setActivatedAccount(profileData.stripeAccountActive);
      } catch (error) {
        console.error("Error fetching coach profile:", error);
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchCoachProfile();
  }, [userId, shouldRedirectToFacility]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get("tab");
    if (tab === "setup") {
      setSelectedTab(2);
    }
    if (tab === "customers") {
      setSelectedTab(1);
    }
  }, [location, history]);

  useEffect(() => {
    if (location.state?.openCustomersTab) {
      setSelectedTab(1);
      history.replace({ ...location, state: {} });
    }
  }, [location, history]);

  // Don't render anything while redirecting to facility billing
  if (shouldRedirectToFacility) {
    return null;
  }

  // If there's an OAuth code and user has a facility but facility data isn't loaded yet,
  // wait before rendering to avoid calling the wrong API endpoint
  const hasOAuthCode = location.search.includes('code=');
  const facilityDataStillLoading = user.facilityId && !user.facility;
  if (hasOAuthCode && facilityDataStillLoading) {
    return null;
  }

  const renderTab = (selectedTab) => {
    if (selectedTab === 0) {
      return <Offerings enabledIntegration={enabledIntegration} />;
    }
    if (selectedTab === 1) {
      return <Customers stripeIntegrationEnabled={enabledIntegration} />;
    }
    return (
      <Setup
        isLoadingData={isLoadingData}
        stripeId={stripeId}
        setStripeId={setStripeId}
        activatedAccount={activatedAccount}
        enabledIntegration={enabledIntegration}
        setEnabledIntegration={setEnabledIntegration}
      />
    );
  };

  const tabs = [
    {
      label: t('Offerings'),
      onClickHandler: () => setSelectedTab(0),
      isActive: selectedTab === 0
    },
    {
      label: t('Customers'),
      onClickHandler: () => setSelectedTab(1),
      isActive: selectedTab === 1
    },
    {
      label: t('Setup'),
      onClickHandler: () => setSelectedTab(2),
      isActive: selectedTab === 2
    }
  ];

  return (
    <div className={styles.position}>
      <Navbar label={t("Billing")} hasSearch={false} hasNotifications={false} />
      <Tabs tabs={tabs} />
      <div className={`${styles.empty_container} billing-page-container`}>
        {renderTab(selectedTab)}
      </div>
    </div>
  );
}
